'use client';

import * as React from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { cn } from '@/lib/utils';
import { cva } from 'class-variance-authority';

// Simpler, working accordion menu without complex nested state
const AccordionMenu = React.forwardRef(({ 
  className, 
  children, 
  type = 'single',
  defaultValue,
  value,
  onValueChange,
  collapsible = true,
  ...props 
}, ref) => {
  return (
    <AccordionPrimitive.Root
      ref={ref}
      type={type}
      defaultValue={defaultValue}
      value={value}
      onValueChange={onValueChange}
      collapsible={collapsible}
      className={cn('w-full', className)}
      {...props}
    >
      {children}
    </AccordionPrimitive.Root>
  );
});
AccordionMenu.displayName = 'AccordionMenu';

const AccordionMenuGroup = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('space-y-0.5', className)}
    {...props}
  />
));
AccordionMenuGroup.displayName = 'AccordionMenuGroup';

const AccordionMenuLabel = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'px-2 py-1 text-xs font-medium text-muted-foreground',
      className
    )}
    {...props}
  />
));
AccordionMenuLabel.displayName = 'AccordionMenuLabel';

const AccordionMenuItem = React.forwardRef(({ className, children, onClick, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className="flex"
    {...props}
  >
    <AccordionPrimitive.Header className="flex w-full">
      <AccordionPrimitive.Trigger
        className={cn(
          'relative cursor-pointer select-none flex w-full text-start items-center text-foreground rounded-lg gap-2 px-2 py-1 text-sm outline-hidden transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
          className
        )}
        onClick={onClick}
      >
        {children}
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  </AccordionPrimitive.Item>
));
AccordionMenuItem.displayName = 'AccordionMenuItem';

const AccordionMenuSub = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Item
    ref={ref}
    className={cn('', className)}
    {...props}
  >
    {children}
  </AccordionPrimitive.Item>
));
AccordionMenuSub.displayName = 'AccordionMenuSub';

const AccordionMenuSubTrigger = React.forwardRef(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      data-slot="accordion-menu-sub-trigger"
      className={cn(
        'group w-full relative flex items-center cursor-pointer select-none text-start rounded-lg gap-2 px-2 py-1 text-sm outline-hidden text-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
        className
      )}
      {...props}
    >
      {children}
      <i
        className="fas fa-chevron-down ms-auto shrink-0 text-muted-foreground transition-transform duration-200 group-data-[state=open]:-rotate-180"
        aria-hidden="true"
      />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionMenuSubTrigger.displayName = 'AccordionMenuSubTrigger';

const AccordionMenuSubContent = React.forwardRef(({
  className,
  children,
  type = 'single',
  collapsible = true,
  parentValue, // Accept but don't pass to DOM
  defaultValue, // Unterpunkt, der offen sein soll (Zweig zur aktuellen Seite) — nicht ans DOM
  ...props
}, ref) => {
  // Unter-Ebene steuerbar: oeffnet den Zweig zur aktuellen Seite (auch nach Seitenwechsel),
  // manuelles Auf-/Zuklappen bleibt moeglich. Ohne defaultValue: wie bisher (alles zu).
  const [subValue, setSubValue] = React.useState(defaultValue ?? (type === 'multiple' ? [] : ''));
  React.useEffect(() => {
    if (defaultValue) setSubValue(defaultValue);
  }, [defaultValue]);
  return (
    <AccordionPrimitive.Content
      ref={ref}
      data-menu-sub=""
      className={cn(
        'ps-5',
        'overflow-hidden',
        'data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down',
        className
      )}
      {...props}
    >
      <AccordionPrimitive.Root
        type={type}
        collapsible={collapsible}
        value={subValue}
        onValueChange={setSubValue}
        className="w-full py-0.5"
      >
        {children}
      </AccordionPrimitive.Root>
    </AccordionPrimitive.Content>
  );
});
AccordionMenuSubContent.displayName = 'AccordionMenuSubContent';

const AccordionMenuSeparator = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('my-1 h-px bg-border', className)}
    {...props}
  />
));
AccordionMenuSeparator.displayName = 'AccordionMenuSeparator';

const AccordionMenuIndicator = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden="true"
    className={cn('ms-auto flex items-center font-medium', className)}
    {...props}
  />
));
AccordionMenuIndicator.displayName = 'AccordionMenuIndicator';

export {
  AccordionMenu,
  AccordionMenuGroup,
  AccordionMenuIndicator,
  AccordionMenuItem,
  AccordionMenuLabel,
  AccordionMenuSeparator,
  AccordionMenuSub,
  AccordionMenuSubContent,
  AccordionMenuSubTrigger,
};